import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getOptionalObject,
  getList,
  getString,
} from "@surya-digital/leo-ts-types";
import { Code, CodeDTO } from "../types/code";
import { QuestionType } from "../types/questionType";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
import { OpenEndedFieldResponseV1, OpenEndedFieldResponseV1DTO } from "./openEndedFieldResponseV1";
import { GridQuestionResponseV1, GridQuestionResponseV1DTO } from "./gridQuestionResponseV1";


export namespace GroupQuestionResponseV1Enums {
  export namespace ChildResponse {
    export interface OpenEndedDTO {
      case: string;
      fieldResponses: OpenEndedFieldResponseV1DTO[];
    }
    
    export class OpenEnded {
      readonly isChildResponseOpenEnded: boolean = true;
    
      readonly fieldResponses: OpenEndedFieldResponseV1[];
    
    
      constructor(
      fieldResponses: OpenEndedFieldResponseV1[]
      ) {
        this.fieldResponses = fieldResponses;
      }
    
      static fromDTO(dto: OpenEndedDTO): OpenEnded {
        const fieldResponses = getList<OpenEndedFieldResponseV1DTO, OpenEndedFieldResponseV1>(
          dto,
          "fieldResponses",
          OpenEndedFieldResponseV1.fromDTO,
          InvalidGroupQuestionResponseV1ChildResponseOpenEndedFieldResponsesError
        );
        return new OpenEnded(
          fieldResponses
        );
      }
    
      toDTO(): OpenEndedDTO {
        const dto: OpenEndedDTO = {
          case: "OPEN_ENDED",
            fieldResponses: this.fieldResponses.map((e: OpenEndedFieldResponseV1) => { return e.toDTO() })
        };
        return dto;
      }
      }
    
    OpenEnded.prototype.toString = function toString(): string {
      return `ChildResponse.OPEN_ENDED(fieldResponses='${this.fieldResponses}')`;
    };
    
    export interface SingleSelectDTO {
      case: string;
      answer: string;
    }
    
    export class SingleSelect {
      readonly isChildResponseSingleSelect: boolean = true;
    
      readonly answer: string;
    
    
      constructor(
      answer: string
      ) {
        SingleSelect.validateAnswer(answer);
        this.answer = answer;
      }
    
      static fromDTO(dto: SingleSelectDTO): SingleSelect {
        const answer = getString(dto, "answer", InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerError);
        return new SingleSelect(
          answer
        );
      }
    
      toDTO(): SingleSelectDTO {
        const dto: SingleSelectDTO = {
          case: "SINGLE_SELECT",
            answer: this.answer
        };
        return dto;
      }
      private static validateAnswer = (answer: string): void => {
        if(answer.trim().length === 0) {
          throw new InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerNonEmptyError(
            `Attribute answer cannot be an empty string.`
          );
        }
      }
      
    }
    
    SingleSelect.prototype.toString = function toString(): string {
      return `ChildResponse.SINGLE_SELECT(answer='${this.answer}')`;
    };
    
    export interface MultiSelectDTO {
      case: string;
      answer: string;
    }
    
    export class MultiSelect {
      readonly isChildResponseMultiSelect: boolean = true;
    
      readonly answer: string;
    
    
      constructor(
      answer: string
      ) {
        MultiSelect.validateAnswer(answer);
        this.answer = answer;
      }
    
      static fromDTO(dto: MultiSelectDTO): MultiSelect {
        const answer = getString(dto, "answer", InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerError);
        return new MultiSelect(
          answer
        );
      }
    
      toDTO(): MultiSelectDTO {
        const dto: MultiSelectDTO = {
          case: "MULTI_SELECT",
            answer: this.answer
        };
        return dto;
      }
      private static validateAnswer = (answer: string): void => {
        if(answer.trim().length === 0) {
          throw new InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerNonEmptyError(
            `Attribute answer cannot be an empty string.`
          );
        }
      }
      
    }
    
    MultiSelect.prototype.toString = function toString(): string {
      return `ChildResponse.MULTI_SELECT(answer='${this.answer}')`;
    };
    
    export interface RankingDTO {
      case: string;
      answer: string;
    }
    
    export class Ranking {
      readonly isChildResponseRanking: boolean = true;
    
      readonly answer: string;
    
    
      constructor(
      answer: string
      ) {
        Ranking.validateAnswer(answer);
        this.answer = answer;
      }
    
      static fromDTO(dto: RankingDTO): Ranking {
        const answer = getString(dto, "answer", InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerError);
        return new Ranking(
          answer
        );
      }
    
      toDTO(): RankingDTO {
        const dto: RankingDTO = {
          case: "RANKING",
            answer: this.answer
        };
        return dto;
      }
      private static validateAnswer = (answer: string): void => {
        if(answer.trim().length === 0) {
          throw new InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerNonEmptyError(
            `Attribute answer cannot be an empty string.`
          );
        }
      }
      
    }
    
    Ranking.prototype.toString = function toString(): string {
      return `ChildResponse.RANKING(answer='${this.answer}')`;
    };
    
    export interface GridDTO {
      case: string;
      answer: GridQuestionResponseV1DTO[];
    }
    
    export class Grid {
      readonly isChildResponseGrid: boolean = true;
    
      readonly answer: GridQuestionResponseV1[];
    
    
      constructor(
      answer: GridQuestionResponseV1[]
      ) {
        this.answer = answer;
      }
    
      static fromDTO(dto: GridDTO): Grid {
        const answer = getList<GridQuestionResponseV1DTO, GridQuestionResponseV1>(
          dto,
          "answer",
          GridQuestionResponseV1.fromDTO,
          InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerError
        );
        return new Grid(
          answer
        );
      }
    
      toDTO(): GridDTO {
        const dto: GridDTO = {
          case: "GRID",
            answer: this.answer.map((e: GridQuestionResponseV1) => { return e.toDTO() })
        };
        return dto;
      }
      }
    
    Grid.prototype.toString = function toString(): string {
      return `ChildResponse.GRID(answer='${this.answer}')`;
    };
    
  
    export type ChildResponse = OpenEnded | SingleSelect | MultiSelect | Ranking | Grid;
  
    export type ChildResponseDTO = OpenEndedDTO | SingleSelectDTO | MultiSelectDTO | RankingDTO | GridDTO;
  
    export const fromDTO = (dto: ChildResponseDTO): ChildResponse => {
      switch (dto.case) {
        case "OPEN_ENDED":
            return OpenEnded.fromDTO(dto as OpenEndedDTO);
        case "SINGLE_SELECT":
            return SingleSelect.fromDTO(dto as SingleSelectDTO);
        case "MULTI_SELECT":
            return MultiSelect.fromDTO(dto as MultiSelectDTO);
        case "RANKING":
            return Ranking.fromDTO(dto as RankingDTO);
        case "GRID":
            return Grid.fromDTO(dto as GridDTO);
        default:
          throw new InvalidGroupQuestionResponseV1ChildResponseError(
              `Case ${dto.case} is not valid case of ChildResponse.`
          );
      }
    };
  }
  
}
export interface GroupQuestionResponseV1DTO {
  questionId: string;
  questionCode: CodeDTO;
  questionType: QuestionType.QuestionTypeDTO;
  question?: SurveyLocalizedTextDTO | null;
  childResponse: GroupQuestionResponseV1Enums.ChildResponse.ChildResponseDTO;
}

export class GroupQuestionResponseV1 {
  readonly isGroupQuestionResponseV1GroupQuestionResponseV1: boolean = true;

  readonly questionId: LeoUUID;
  readonly questionCode: Code;
  readonly questionType: QuestionType.QuestionType;
  readonly question: SurveyLocalizedText | null;
  readonly childResponse: GroupQuestionResponseV1Enums.ChildResponse.ChildResponse;


  constructor(
  questionId: LeoUUID, 
  questionCode: Code, 
  questionType: QuestionType.QuestionType, 
  question: SurveyLocalizedText | null = null, 
  childResponse: GroupQuestionResponseV1Enums.ChildResponse.ChildResponse
  ) {
    this.questionId = questionId;
    this.questionCode = questionCode;
    this.questionType = questionType;
    this.question = question;
    this.childResponse = childResponse;
  }

  static fromDTO(dto: GroupQuestionResponseV1DTO): GroupQuestionResponseV1 {
    const questionId = getLeoUuid(dto, "questionId", InvalidGroupQuestionResponseV1QuestionIdError);
    const questionCode = Code.fromDTO(getObject(dto, "questionCode", InvalidGroupQuestionResponseV1QuestionCodeError));
    const questionType = QuestionType.fromDTO(getObject(dto, "questionType", InvalidGroupQuestionResponseV1QuestionTypeError));
    let question = getOptionalObject(dto, "question");
    if(question !== undefined && question !== null) {
      question = SurveyLocalizedText.fromDTO(getObject(dto, "question", InvalidGroupQuestionResponseV1QuestionError));
    }
    const childResponse = GroupQuestionResponseV1Enums.ChildResponse.fromDTO(getObject(dto, "childResponse", InvalidGroupQuestionResponseV1ChildResponseError));
    return new GroupQuestionResponseV1(
      questionId,
      questionCode,
      questionType,
      question,
      childResponse
    );
  }

  toDTO(): GroupQuestionResponseV1DTO {
    const dto: GroupQuestionResponseV1DTO = {
        questionId: this.questionId.toDTO(),
        questionCode: this.questionCode.toDTO(),
        questionType: QuestionType.toDTO(this.questionType),
        childResponse: this.childResponse.toDTO()
    };
    if (this.question !== undefined && this.question !== null){
      dto.question = this.question.toDTO();
    }
    return dto;
  }
  }

GroupQuestionResponseV1.prototype.toString = function toString(): string {
  return `GroupQuestionResponseV1(questionId='${this.questionId}',questionCode='${this.questionCode}',questionType='${this.questionType}',question='${this.question}',childResponse='${this.childResponse}')`;
};

export class InvalidGroupQuestionResponseV1Error extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1Error";
  }
}
export class InvalidGroupQuestionResponseV1QuestionIdError extends InvalidGroupQuestionResponseV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1QuestionIdError";
  }
}

export class InvalidGroupQuestionResponseV1QuestionCodeError extends InvalidGroupQuestionResponseV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1QuestionCodeError";
  }
}

export class InvalidGroupQuestionResponseV1QuestionTypeError extends InvalidGroupQuestionResponseV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1QuestionTypeError";
  }
}

export class InvalidGroupQuestionResponseV1QuestionError extends InvalidGroupQuestionResponseV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1QuestionError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseError extends InvalidGroupQuestionResponseV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseOpenEndedError extends InvalidGroupQuestionResponseV1ChildResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseOpenEndedError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseOpenEndedFieldResponsesError extends InvalidGroupQuestionResponseV1ChildResponseOpenEndedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseOpenEndedFieldResponsesError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseSingleSelectError extends InvalidGroupQuestionResponseV1ChildResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseSingleSelectError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerError extends InvalidGroupQuestionResponseV1ChildResponseSingleSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerNonEmptyError extends InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseSingleSelectAnswerNonEmptyError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseMultiSelectError extends InvalidGroupQuestionResponseV1ChildResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseMultiSelectError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseMultiSelectAnswerError extends InvalidGroupQuestionResponseV1ChildResponseMultiSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseMultiSelectAnswerError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseMultiSelectAnswerNonEmptyError extends InvalidGroupQuestionResponseV1ChildResponseMultiSelectAnswerError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseMultiSelectAnswerNonEmptyError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseRankingError extends InvalidGroupQuestionResponseV1ChildResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseRankingError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseRankingAnswerError extends InvalidGroupQuestionResponseV1ChildResponseRankingError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseRankingAnswerError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseRankingAnswerNonEmptyError extends InvalidGroupQuestionResponseV1ChildResponseRankingAnswerError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseRankingAnswerNonEmptyError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseGridError extends InvalidGroupQuestionResponseV1ChildResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseGridError";
  }
}

export class InvalidGroupQuestionResponseV1ChildResponseGridAnswerError extends InvalidGroupQuestionResponseV1ChildResponseGridError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGroupQuestionResponseV1ChildResponseGridAnswerError";
  }
}
